import { CaretRightIcon, GlobeIcon, QuestionIcon, ShieldWarningIcon, WarningIcon } from '@phosphor-icons/react'
import * as Collapsible from '@radix-ui/react-collapsible'
import classNames from 'classnames'
import { useRef } from 'react'
import { Link, useSearchParams } from 'react-router'
import { match } from 'ts-pattern'
import { z } from 'zod'
import * as labelStyles from '../../components/composition-bar/segments-labels.css.ts'
import { ScaleBar } from '../../components/scale-bar.tsx'
import { ScorecardTotalDonut } from '../../components/scorecard-total-donut.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { Routes } from '../../routes.ts'
import { joinWithAnd, pluralize } from '../../utils/text-utils.ts'
import { TONES } from '../../utils/threat-model/exposure-tone.css.ts'
import { POSTURE_RAMP } from '../../utils/threat-model/scorecard-bands.css.ts'
import { BAND_LABELS, POSTURE_BAND_ORDER } from '../../utils/threat-model/scorecard-bands.ts'
import { DimensionPostureTable } from './dimension-posture-table.tsx'
import { EstateCompositionBar } from './estate-composition-bar.tsx'
import type { EstateFreshness } from './estate-freshness.ts'
import { ExposureBar } from './exposure-bar.tsx'
import { LeastDefendedTable } from './least-defended-table.tsx'
import type { OrgScorecardPosture } from './org-scorecard-posture.ts'
import type { OrgThreatPosture } from './org-threat-posture.ts'
import * as styles from './security-posture-page.css.ts'
import { useOrgThreatPosture } from './use-org-threat-posture.ts'
import { WidespreadVulnerabilitiesTable } from './widespread-vulnerabilities-table.tsx'

/**
 * The two tables are tabs rather than stacked sections: they answer different questions — "which
 * vulnerabilities recur" and "which repositories are worst covered" — and a reader wants one at a time.
 * The estate figures stay above both, because they frame either answer.
 */
const TabSchema = z.enum(['vulnerabilities', 'repositories', 'posture'])
type PostureTab = z.infer<typeof TabSchema>

/**
 * Posture by dimension leads, because the estate card's first figure is the estate posture and this is the
 * tab that decomposes it — the number and its breakdown belong next to each other. Then the repositories
 * worth naming, then the classes that recur: summary, then who, then what.
 */
const TABS: { value: PostureTab; label: string }[] = [
  { value: 'posture', label: 'Posture by dimension' },
  { value: 'repositories', label: 'Least defended repositories' },
  { value: 'vulnerabilities', label: 'Widespread vulnerabilities' },
]

const DEFAULT_TAB: PostureTab = 'posture'

const parseTab = (raw: string | null): PostureTab => TabSchema.catch(DEFAULT_TAB).parse(raw)

/**
 * The estate's security posture: how exposed it is, and which vulnerabilities recur across it.
 *
 * Two sections, in that order, because they answer "should I care" before "about what". Both are
 * aggregated client-side from per-repository threat models — there is no org-level endpoint — so the
 * page is honest about being partial while the fan-out lands.
 */
export const SecurityPosturePage: React.FC = () => {
  const {
    posture,
    freshness,
    scorecardPosture,
    isLoadingRepositories,
    isAggregating,
    settledCount,
    requestCount,
    unreadRepositoryCount,
  } = useOrgThreatPosture()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = parseTab(searchParams.get('tab'))
  const tabRefs = useRef(new Map<PostureTab, HTMLButtonElement>())

  const selectTab = (tab: PostureTab) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        // The default tab leaves no trace, so a shared link is only ever longer than it needs to be
        // when it is saying something.
        if (tab === DEFAULT_TAB) {
          next.delete('tab')
        } else {
          next.set('tab', tab)
        }
        return next
      },
      { replace: true }
    )

  // Roving tabindex with automatic activation — moving focus selects, matching the Organization page.
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TABS.findIndex(tab => tab.value === activeTab)
    const lastIndex = TABS.length - 1
    const nextIndex = match(event.key)
      .with('ArrowRight', () => (currentIndex === lastIndex ? 0 : currentIndex + 1))
      .with('ArrowLeft', () => (currentIndex === 0 ? lastIndex : currentIndex - 1))
      .with('Home', () => 0)
      .with('End', () => lastIndex)
      .otherwise(() => currentIndex)
    if (nextIndex === currentIndex) {
      return
    }
    const nextTab = TABS[nextIndex]
    if (!nextTab) {
      return
    }
    event.preventDefault()
    selectTab(nextTab.value)
    tabRefs.current.get(nextTab.value)?.focus()
  }

  if (isLoadingRepositories) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading security posture" />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Security posture</h1>
        <p className={styles.subtitle}>
          What threat models say collectively about the organization, aggregated across all repositories.
        </p>
        {isAggregating && (
          <p className={styles.progress}>
            {`Reading ${settledCount} of ${requestCount} ${pluralize(requestCount, 'repository', 'repositories')} — figures will move as the rest arrive.`}
          </p>
        )}
      </header>

      {posture.estate.modelledCount === 0 ? (
        <div className={styles.body}>
          <NothingModelledYet posture={posture} />
        </div>
      ) : (
        <>
          <div className={styles.body}>
            <EstateFigures
              posture={posture}
              scorecardPosture={scorecardPosture}
              unreadRepositoryCount={unreadRepositoryCount}
            />
          </div>

          <div className={styles.tabRow} role="tablist" aria-label="Security posture views">
            {TABS.map(({ value, label }) => {
              const isSelected = activeTab === value
              const count = match(value)
                .with('vulnerabilities', () => posture.vulnerabilities.length)
                .with('repositories', () => posture.worstCovered.length)
                .with('posture', () => scorecardPosture.dimensions.length)
                .exhaustive()
              return (
                <button
                  key={value}
                  ref={node => {
                    if (node) {
                      tabRefs.current.set(value, node)
                    } else {
                      tabRefs.current.delete(value)
                    }
                  }}
                  type="button"
                  role="tab"
                  id={`tab-${value}`}
                  aria-controls={`tabpanel-${value}`}
                  aria-selected={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  className={styles.tab}
                  onClick={() => selectTab(value)}
                  onKeyDown={handleTabKeyDown}
                >
                  {label}
                  <span className={styles.tabBadge}>{count}</span>
                </button>
              )
            })}
          </div>

          {/* All panels stay mounted so the scroll position and any open disclosure survive a switch;
              `hidden` alone would be overridden by the panel's own `display`. Ordered to match `TABS`, so
              the tab sequence a keyboard walks matches the one an eye reads. */}
          <div
            id="tabpanel-posture"
            role="tabpanel"
            aria-labelledby="tab-posture"
            hidden={activeTab !== 'posture'}
            className={styles.body}
          >
            <RiskPosture scorecardPosture={scorecardPosture} />
          </div>

          <div
            id="tabpanel-repositories"
            role="tabpanel"
            aria-labelledby="tab-repositories"
            hidden={activeTab !== 'repositories'}
            className={styles.body}
          >
            <WorstCovered posture={posture} freshness={freshness} />
          </div>

          <div
            id="tabpanel-vulnerabilities"
            role="tabpanel"
            aria-labelledby="tab-vulnerabilities"
            hidden={activeTab !== 'vulnerabilities'}
            className={styles.body}
          >
            <WidespreadVulnerabilities posture={posture} />
          </div>
        </>
      )}
    </div>
  )
}

const NothingModelledYet: React.FC<{ posture: OrgThreatPosture }> = ({ posture }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyTitle}>No threat models yet</p>
    <p className={styles.emptyBody}>
      {posture.estate.repositoryCount === 0
        ? 'Add a repository to start building a picture of the estate.'
        : `None of the ${posture.estate.repositoryCount} repositories here has a generated threat model. Generate one from a repository's Threat model tab and it will appear in these figures.`}
    </p>
  </div>
)

const EstateFigures: React.FC<{
  posture: OrgThreatPosture
  scorecardPosture: OrgScorecardPosture
  unreadRepositoryCount: number
}> = ({ posture, scorecardPosture, unreadRepositoryCount }) => {
  const { estate } = posture

  return (
    <section className={styles.section} aria-label="Estate">
      <div className={styles.estateCard}>
        <div className={styles.estateRow}>
          {/* Coverage leads, because it is the denominator every other figure here is measured over: an
            estate figure means nothing until you know how much of the estate was looked at. */}
          {/* The bottom line, first: it is the one figure answering "how are we doing" rather than "how
              much do we know". Everything after it qualifies it. */}
          <PostureFigure scorecardPosture={scorecardPosture} modelledCount={estate.modelledCount} />
          {/* The two coverage figures and the bar that is the denominator for both, as one cell. */}
          <div className={styles.coverageGroup}>
            <GlobeIcon className={styles.cellIcon} weight="light" aria-hidden />
            <div className={styles.coverageFigures}>
              <ModelledFigure posture={posture} />
              {/* No note: the bar's caption below this cell already defines reachability, and saying it
                  twice in one cell made the pair read as two unrelated figures. */}
              <Figure
                value={estate.internetFacingCount}
                outOf={estate.modelledCount}
                label="Repositories are internet-facing"
              />
            </div>
            <div className={styles.coverageBar}>
              <ExposureBar
                exposureMix={posture.exposureMix}
                modelledCount={estate.modelledCount}
                unmodelledCount={posture.withoutModel.length}
              />
            </div>
          </div>
          {/* The undefended count and the same count sharpened by exposure, over the bar that splits one
              into the other. An intersection rather than a slice: an undefended vulnerability behind a
              boundary and the same one on a public surface are not the same problem. */}
          <div className={styles.coverageGroup}>
            <ShieldWarningIcon className={styles.cellIcon} weight="light" aria-hidden />
            <div className={styles.gapFigures}>
              <Figure
                value={estate.gapCount}
                label="Vulnerabilities with no control"
                note={`Across ${estate.repositoriesWithGapsCount} ${pluralize(
                  estate.repositoriesWithGapsCount,
                  'repository',
                  'repositories'
                )}`}
                isAlarm
              />
              <Figure
                value={estate.internetFacingGapCount}
                outOf={estate.gapCount}
                label="Vulnerabilities are exposed to the internet"
                note="The rest sit behind a boundary the model describes"
                isAlarm
              />
            </div>
            <div className={styles.coverageBar}>
              <GapExposureBar gapCount={estate.gapCount} internetFacingGapCount={estate.internetFacingGapCount} />
            </div>
          </div>
        </div>
        <div className={styles.estateDisclosureBand}>
          <UnmodelledDisclosure posture={posture} />
          <CapDisclosure coveredCount={estate.repositoryCount} unreadRepositoryCount={unreadRepositoryCount} />
        </div>
      </div>
    </section>
  )
}

type FigureProps = {
  value: number
  /** Renders as a quieter "of N" beside the count. Omit where the count has no denominator. */
  outOf?: number
  /** Reads as a predicate on the count — "Are reachable from…" — so the card is one sentence. */
  label: string
  /** Omit where the bar's own caption below already says it, rather than saying it twice in one cell. */
  note?: string
  isAlarm?: boolean
}

const Figure: React.FC<FigureProps> = ({ value, outOf, label, note, isAlarm = false }) => (
  <div className={styles.figure}>
    {/* `isAlarm` says this metric counts a problem; the colour only lands when there is one. A red zero
        would report the healthiest possible estate in the same tone as the worst. */}
    <p className={isAlarm && value > 0 ? styles.figureValueAlarm : styles.figureValue}>
      {value}
      {outOf !== undefined && <span className={styles.figureDenominatorInline}>of {outOf}</span>}
    </p>
    <p className={styles.figureLabel}>{label}</p>
    {note !== undefined && <p className={styles.figureDenominator}>{note}</p>}
  </div>
)

/**
 * What the figure means, and which way it runs.
 *
 * The scale bar carries the direction, so the prose does not have to: two named ends say it faster than a
 * sentence, and double as a key for the colours the band tags below are drawn in. That is why the copy
 * here says what the number *is* and leaves "higher is better" to the bar.
 *
 * A hint rather than the repositories page's drawer, deliberately. That drawer documents ten dimensions
 * and earns a click; this is one figure and two sentences, and a reader who has to open something to
 * learn which way a number runs has already been failed.
 */
const AboutPostureHint: React.FC<{ rubricVersions: string[] }> = ({ rubricVersions }) => (
  <TooltipWithHover
    side="bottom"
    maxWidth={320}
    trigger={
      <button type="button" className={styles.aboutPostureButton} aria-label="About estate posture">
        <QuestionIcon size={14} weight="bold" aria-hidden />
      </button>
    }
  >
    <span className={styles.hintBody}>
      <span>
        The mean of every scored repository&rsquo;s scorecard total. It measures what is in place, not what is missing
        (therefore a repository&rsquo;s risk score will run the opposite direction).
      </span>
      <ScaleBar stops={POSTURE_RAMP} labels={POSTURE_BAND_ORDER.map(band => BAND_LABELS[band])} />
      {/* Under the scale, because it qualifies the number rather than explaining it, and only when it
          applies — on a single-rubric estate there is nothing to caveat. The denominator line says how
          many versions; this is the only place that names them and says why it matters. */}
      {rubricVersions.length > 1 && (
        <span>
          <span className={styles.hintNoteLabel}>Note: </span>
          The scores come from {joinWithAnd(rubricVersions)}, so they are not strictly comparable and neither is the
          mean across them. A repository is rescored on its next threat-model run, which is what brings the estate back
          onto one rubric.
        </span>
      )}
    </span>
  </TooltipWithHover>
)

/**
 * The estate's posture as one number: the mean of the per-repository scorecard totals.
 *
 * **Posture, not risk** — higher is better here, and the repositories page's risk column runs the other
 * way. The two do not convert: this omits exposure, which only means anything per repository. See
 * `org-scorecard-posture.ts` for why the roll-up is built on scorecards rather than on that column.
 */
const PostureFigure: React.FC<{ scorecardPosture: OrgScorecardPosture; modelledCount: number }> = ({
  scorecardPosture,
  modelledCount,
}) => {
  const { estatePosture, scorecardCount, rubricVersions } = scorecardPosture

  const scope = `Mean of ${scorecardCount} scored ${pluralize(scorecardCount, 'repository', 'repositories')}`

  return (
    <div className={classNames(styles.scoreFigure, styles.estateSpanningCell)}>
      {/* A ring rather than a bare percentage, and the same ring the repository's own Scorecard tab uses —
          so the estate figure and the per-repository one are visibly the same kind of number. It carries no
          inline "of N": the four figures beside it are genuinely "N of M" proportions, which trains the
          reader to read "of" as a denominator, and this value is a mean score rather than a share of
          anything. "70% of 64 scored" read as "70% of 64 were scored", a different and wrong statement. */}
      <ScorecardTotalDonut
        score={estatePosture}
        scoredCount={scorecardCount}
        totalCount={modelledCount}
        subject="Estate posture"
        unitLabel="modelled repositories"
        size="compact"
        valueTone="plain"
      />
      <div className={styles.scoreFigureProse}>
        <p className={styles.scoreFigureLabel}>
          Estate posture
          <AboutPostureHint rubricVersions={rubricVersions} />
        </p>
        <p className={styles.figureDenominator}>
          {/* A count here, not the version names: this line is the figure's denominator, and a second
              hint icon beside it put two question marks in one small block. Which versions they are, and
              why a mixed estate matters, live in the one hint on the label above. */}
          {rubricVersions.length > 1 ? `${scope} across ${rubricVersions.length} rubric versions` : scope}
        </p>
      </div>
    </div>
  )
}

/** How much of the estate has been looked at. Which repositories have not is its own band below. */
const ModelledFigure: React.FC<{ posture: OrgThreatPosture }> = ({ posture }) => (
  <div className={styles.figure}>
    <p className={styles.figureValue}>
      {posture.estate.modelledCount}
      <span className={styles.figureDenominatorInline}>of {posture.estate.repositoryCount}</span>
    </p>
    <p className={styles.figureLabel}>Repositories have a threat model</p>
  </div>
)

/**
 * What is missing from every figure above, and the way to act on it.
 *
 * Its own band at the foot of the card rather than tucked inside the first figure: it qualifies all four
 * figures, not only the one it shares a denominator with, and expanding it in a grid cell pushed the
 * figures beside it around.
 */
const UnmodelledDisclosure: React.FC<{ posture: OrgThreatPosture }> = ({ posture }) => {
  const { withoutModel } = posture

  if (withoutModel.length === 0) {
    return <p className={styles.figureDenominator}>Every repository has a threat model.</p>
  }

  return (
    <Collapsible.Root>
      <Collapsible.Trigger className={styles.discloseTrigger}>
        <span className={styles.discloseLabel}>
          {`${withoutModel.length} ${pluralize(withoutModel.length, 'repository', 'repositories')} not modelled yet`}
          <WarningIcon className={styles.discloseWarning} size={16} weight="fill" aria-hidden />
        </span>
        <CaretRightIcon className={styles.discloseCaret} size={13} weight="bold" aria-hidden />
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ul className={styles.discloseList}>
          {withoutModel.map(repository => (
            <li key={repository.repositoryId}>
              <Link
                className={styles.discloseItem}
                to={`${Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.repositoryId })}?tab=threat-model`}
              >
                {repository.name}
              </Link>
            </li>
          ))}
        </ul>
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

/**
 * The uncontrolled vulnerabilities, split by whether an untrusted caller can reach them.
 *
 * The same shape as the coverage bar: the whole is the figure to its left, and the colours are the split
 * the figure beside it states. It borrows the exposure vocabulary rather than inventing one — `yes` for the
 * reachable share, `no` for the share behind a boundary — so a reader who has learned the bar above it
 * already knows what these mean.
 *
 * Its total is `gapCount`, not every vulnerability the estate has: the aggregate does not carry an
 * estate-wide vulnerability count, and inventing a denominator for a bar is worse than scoping it honestly.
 */
const GapExposureBar: React.FC<{ gapCount: number; internetFacingGapCount: number }> = ({
  gapCount,
  internetFacingGapCount,
}) => {
  const behindBoundaryCount = Math.max(0, gapCount - internetFacingGapCount)

  return (
    <EstateCompositionBar
      total={gapCount}
      rows={[
        {
          key: 'internet-facing',
          name: 'On internet-facing repositories',
          count: internetFacingGapCount,
          color: TONES.yes,
        },
        { key: 'behind-boundary', name: 'Behind a boundary', count: behindBoundaryCount, color: TONES.no },
      ]}
      heading="Exposure: Vulnerabilities with no control"
      ariaPrefix="Uncontrolled vulnerabilities by exposure."
      icon={<ShieldWarningIcon className={labelStyles.leadingIconSvg} weight="light" />}
      note={`All ${gapCount} ${pluralize(gapCount, 'vulnerability', 'vulnerabilities')} with no security control against them, split by reachability.`}
      emptyMessage="Every vulnerability the models found has a control naming it."
      unit={{ one: 'vulnerability', many: 'vulnerabilities' }}
    />
  )
}

/**
 * That the figures above cover less of the estate than the estate contains.
 *
 * Every page of the repository list is read now, so this is silent in the healthy case and speaks only
 * when a page failed. It stays because the failure it guards is the same one the removed cap created: a
 * partial estate presented as a whole one, with every figure quietly measured over a subset.
 */
const CapDisclosure: React.FC<{ coveredCount: number; unreadRepositoryCount: number }> = ({
  coveredCount,
  unreadRepositoryCount,
}) => {
  if (unreadRepositoryCount === 0) {
    return undefined
  }

  const total = coveredCount + unreadRepositoryCount

  return (
    <p className={styles.capNote}>
      <WarningIcon className={styles.discloseWarning} size={14} weight="fill" aria-hidden />
      {`These figures cover ${coveredCount} of ${total} repositories — ${unreadRepositoryCount} could not be read.`}
    </p>
  )
}

/**
 * What the estate's scorecards say, dimension by dimension.
 *
 * A third tab rather than a table stacked below the others: it answers a different question from the two
 * beside it — not "what recurs" or "which repositories are worst" but "where is the estate weak".
 */
const RiskPosture: React.FC<{ scorecardPosture: OrgScorecardPosture }> = ({ scorecardPosture }) => (
  <section className={styles.section} aria-label="Posture by dimension">
    <p className={styles.subtitle}>
      {`Each dimension of the rubric, averaged over the ${scorecardPosture.scorecardCount} ${pluralize(scorecardPosture.scorecardCount, 'repository', 'repositories')} with a scorecard. A mean hides its spread, so the Below adequate column counts the repositories actually in trouble on that dimension — those scoring Weak or Fair.`}
    </p>
    <DimensionPostureTable dimensions={scorecardPosture.dimensions} scorecardCount={scorecardPosture.scorecardCount} />
  </section>
)

const WidespreadVulnerabilities: React.FC<{ posture: OrgThreatPosture }> = ({ posture }) => (
  <section className={styles.section} aria-label="Widespread vulnerabilities">
    <p className={styles.subtitle}>
      One row per vulnerability, not per repository — spellings are merged, so a class counted here is the same class
      wherever it appears.
    </p>
    <WidespreadVulnerabilitiesTable vulnerabilities={posture.vulnerabilities} />
  </section>
)

/**
 * Capped, because this is a ranking rather than an inventory. Unbounded it listed every repository with a
 * gap, which on a real estate is most of it — a hundred rows of the same shape, where the point is the few
 * at the top. The count of what is not shown is stated rather than dropped in silence.
 */
const WORST_COVERED_LIMIT = 10

const WorstCovered: React.FC<{ posture: OrgThreatPosture; freshness: EstateFreshness }> = ({ posture, freshness }) => {
  const shown = posture.worstCovered.slice(0, WORST_COVERED_LIMIT)
  const hiddenCount = posture.worstCovered.length - shown.length

  return (
    <section className={styles.section} aria-label="Least defended repositories">
      <p className={styles.subtitle}>
        {hiddenCount > 0
          ? `Ranked by the share of each repository's vulnerabilities that no control names. Showing ${shown.length} of ${posture.worstCovered.length} repositories with at least one gap; fully defended repositories are left out rather than listed at zero.`
          : "Ranked by the share of each repository's vulnerabilities that no control names. Fully defended repositories are left out rather than listed at zero."}
      </p>
      <LeastDefendedTable
        repositories={shown}
        modelFreshness={{
          bandByRepository: freshness.bandByRepository,
          staleAfterDays: freshness.staleAfterDays,
          recentWithinDays: freshness.recentWithinDays,
        }}
      />
    </section>
  )
}
